/**
 * Pets: what you hatch, what you feed it, and what it is allowed to change.
 *
 * The design is [Pets](../../docs/PETS.md). The hard part is not the creatures;
 * it is that pets grant **buffs** and food is **buyable**, which runs straight
 * into [D-020](../../docs/DECISIONS.md#d-020) — everything purchasable must be
 * strictly cosmetic. [D-038](../../docs/DECISIONS.md#d-038) draws the line:
 * buffs touch the economy, never the teaching.
 *
 * **That line is a type here, not a rule somebody has to remember**
 * ([D-105](../../docs/DECISIONS.md#d-105)). A buff is not a function and not a
 * number — it is a value from a closed set of `BuffSurface`s, every one of which
 * is on the permitted side of that table. There is no surface for *how hard a
 * question is*, *how many options it has*, *whether a hint is available*,
 * *whether an answer counts as correct*, or *what the path unlocks*. Not a
 * forbidden one; **no representation at all**. A future pet that wanted to make
 * questions easier could not be written down.
 *
 * Three more things the design insists on, encoded rather than documented:
 *
 * - **Eggs hatch by doing lessons, never by waiting.** There is no timestamp in
 *   an egg — only a lesson count — so the "come back in four hours" pattern
 *   refused for chests ([D-021](../../docs/DECISIONS.md#d-021)) is not
 *   expressible either.
 * - **A hungry pet naps.** There is no sad state, no decay, nothing that can be
 *   lost. `happy` and `napping` are the whole of it, and feeding is the only
 *   transition between them.
 * - **Play-days, not calendar days.** A pet gets hungry only on days the child
 *   actually played, so nobody comes back from a holiday to a starving animal.
 *   The type carries a play-day counter and no `Date` at all, which is what
 *   makes that true rather than merely intended.
 */
// --- what a buff is allowed to touch ----------------------------------------
/**
 * The surfaces a pet may change. **This list is the safety property.**
 *
 * Every entry appears in the ✅ column of
 * [Pets](../../docs/PETS.md#the-line-that-makes-this-safe); nothing in the ❌
 * column has an entry, so it cannot be built. Adding one means editing this
 * type, its exhaustive switch, the test that pins this list, and the document —
 * which is the amount of friction a rule about children's learning deserves.
 */
export const BUFF_SURFACES = [
    /** How many acorns or XP a thing pays. */
    'rewards',
    /** How many **review items** a lesson contains. More practice, not less. */
    'practice',
    /** Whether one mistake breaks a combo. */
    'combo',
    /** How often a bonus round appears. */
    'bonusRounds',
];
/**
 * The most any pet may multiply a reward by.
 *
 * [Pets](../../docs/PETS.md#balance) sets the gap between a fully-fed legendary
 * and no pet at all at about +25% on rewards, *not* multiples. Checked against
 * the catalogue by a test rather than clamped at runtime: a cap the catalogue
 * itself breaks is not a cap, and silently clamping a pet to less than its card
 * promises is how the words and the behaviour drift apart.
 */
export const MAX_REWARD_BUFF = 1.25;
/**
 * Which slice of the economy a reward buff applies to.
 *
 * Narrow on purpose. `all` is the only one that touches everything a child
 * earns, and it is the only one the +25% cap constrains — a pet that pays half
 * as much again *on daily quests* is a much smaller bonus than the number
 * suggests, and a far more interesting one than another flat multiplier.
 * Flattening these into one global rate would make every pet the same pet with
 * a different picture.
 */
export const PAYOUTS = ['lesson', 'speaking', 'quests', 'hardMode', 'chests'];
export const REWARD_STREAMS = ['all', ...PAYOUTS];
/** Does a buff on this stream apply to that payout? */
export const covers = (stream, payout) => stream === 'all' || stream === payout;
export const PET_RARITIES = ['common', 'rare', 'epic', 'legendary'];
/**
 * The ten pets ([Pets](../../docs/PETS.md#the-ten-pets)).
 *
 * Elemental creatures rather than animals, because the twenty avatar animals
 * are *you* and a pet that could also be your avatar is a confusion no
 * eight-year-old should have to resolve.
 *
 * **Sprout is the one to notice.** Its bonus is *more practice* — a buff whose
 * reward is more learning — and it is common, so every child gets one early.
 */
export const PETS = [
    {
        id: 'sprout',
        nameL1: 'Sprout',
        emoji: '🌱',
        rarity: 'common',
        buff: { surface: 'practice', extraReviewItems: 2 },
        says: 'Sprout gives you 2 extra practice words in every lesson.',
    },
    {
        id: 'pebble',
        nameL1: 'Pebble',
        emoji: '🪨',
        rarity: 'common',
        buff: { surface: 'combo', forgivesFirstMistake: true },
        says: 'With Pebble, your first mistake does not break your streak.',
    },
    {
        id: 'puff',
        nameL1: 'Puff',
        emoji: '☁️',
        rarity: 'common',
        buff: { surface: 'rewards', stream: 'all', xp: 1.2, acorns: 1 },
        says: 'Puff gives you a bit more XP for everything you do.',
    },
    {
        id: 'ember',
        nameL1: 'Ember',
        emoji: '🔥',
        rarity: 'rare',
        buff: { surface: 'rewards', stream: 'all', xp: 1, acorns: 1.25 },
        says: 'Ember finds you extra acorns.',
    },
    {
        id: 'drip',
        nameL1: 'Drip',
        emoji: '💧',
        rarity: 'rare',
        buff: { surface: 'rewards', stream: 'speaking', xp: 1, acorns: 2 },
        says: 'Drip pays double acorns when you say things out loud.',
    },
    {
        id: 'twinkle',
        nameL1: 'Twinkle',
        emoji: '✨',
        rarity: 'rare',
        buff: { surface: 'bonusRounds', multiplier: 1.5 },
        says: 'Twinkle brings bonus rounds along more often.',
    },
    {
        id: 'bloom',
        nameL1: 'Bloom',
        emoji: '🌸',
        rarity: 'rare',
        buff: { surface: 'rewards', stream: 'quests', xp: 1, acorns: 1.5 },
        says: 'Bloom makes your daily quests pay half as much again.',
    },
    {
        id: 'zap',
        nameL1: 'Zap',
        emoji: '⚡',
        rarity: 'epic',
        buff: { surface: 'rewards', stream: 'hardMode', xp: 1.5, acorns: 1.5 },
        says: 'Zap loves hard mode, and pays extra every time you use it.',
    },
    {
        id: 'frost',
        nameL1: 'Frost',
        emoji: '❄️',
        rarity: 'epic',
        buff: { surface: 'rewards', stream: 'chests', xp: 1, acorns: 1.3 },
        says: 'Frost finds extra acorns in every chest.',
    },
    {
        id: 'prism',
        nameL1: 'Prism',
        emoji: '🌈',
        rarity: 'legendary',
        buff: { surface: 'mirror' },
        says: 'Prism can borrow any other pet’s bonus. Pick one each day.',
    },
];
export const petById = (id) => PETS.find((pet) => pet.id === id);
// --- eggs -------------------------------------------------------------------
export const EGG_KINDS = ['speckled', 'striped', 'shining'];
export const EGGS = {
    speckled: { lessons: 5, rarities: ['common'] },
    striped: { lessons: 10, rarities: ['rare'] },
    shining: { lessons: 15, rarities: ['epic', 'legendary'] },
};
/** How far along an egg is, and what to say about it. */
export function hatchProgress(egg) {
    const needed = EGGS[egg.kind].lessons;
    const done = Math.min(egg.lessonsDone, needed);
    const left = needed - done;
    return {
        ready: left <= 0,
        done,
        needed,
        says: left <= 0
            ? "It's wobbling! Tap to see who is inside."
            : left === 1
                ? 'One more lesson and it hatches!'
                : `${String(left)} more lessons and it hatches!`,
    };
}
/**
 * The same generator the chests use ([`chests.ts`](./chests.ts)), for the same
 * reason: a hatch must give the same answer when replayed from its seed.
 */
function generator(seed) {
    let hash = 2166136261;
    for (let at = 0; at < seed.length; at++) {
        hash ^= seed.charCodeAt(at);
        hash = Math.imul(hash, 16777619);
    }
    let state = hash >>> 0;
    return () => {
        state = (state + 0x6d2b79f5) >>> 0;
        let drawn = state;
        drawn = Math.imul(drawn ^ (drawn >>> 15), drawn | 1);
        drawn ^= drawn + Math.imul(drawn ^ (drawn >>> 7), drawn | 61);
        return ((drawn ^ (drawn >>> 14)) >>> 0) / 4294967296;
    };
}
/**
 * Who is inside.
 *
 * **Never a duplicate**, the same rule as chests
 * ([D-021](../../docs/DECISIONS.md#d-021)): a child who opens an egg and gets
 * the pet they already have has been given nothing. When every pet in the egg's
 * band is owned it widens to the others rather than paying out an empty egg —
 * an egg is a creature, and there is no consolation version of one.
 *
 * Null only when all ten are owned, which is the whole catalogue and a thing
 * worth celebrating rather than a failure.
 */
export function hatch(input) {
    const random = generator(input.seed);
    const bands = EGGS[input.egg.kind].rarities;
    // The egg's own bands first, then everything else, so widening is a
    // fallback rather than a shuffle.
    const order = [
        ...bands,
        ...PET_RARITIES.filter((rarity) => !bands.includes(rarity)),
    ];
    for (const rarity of order) {
        // Sorted before drawing, so the result depends on the seed rather than on
        // the order a database happened to return rows in.
        const candidates = PETS.filter((pet) => pet.rarity === rarity && !input.owned.has(pet.id)).sort((a, b) => (a.id < b.id ? -1 : 1));
        const pet = candidates[Math.floor(random() * candidates.length)];
        if (pet !== undefined)
            return pet;
    }
    return null;
}
// --- food -------------------------------------------------------------------
export const FOODS = ['berries', 'nuts', 'honeycomb', 'starfruit'];
export const FOOD = {
    berries: { nameL1: 'Berries', emoji: '🍓', priceAcorns: 120, playDays: 1 },
    nuts: { nameL1: 'Nuts', emoji: '🌰', priceAcorns: 220, playDays: 2 },
    honeycomb: { nameL1: 'Honeycomb', emoji: '🍯', priceAcorns: 400, playDays: 4 },
    starfruit: { nameL1: 'Star Fruit', emoji: '⭐', priceAcorns: null, playDays: 7 },
};
/**
 * Happy, or napping. There is no third state and nothing below napping.
 *
 * [Pets](../../docs/PETS.md#the-rule-that-keeps-this-kind): a hungry pet is
 * never sad, sick, hurt or in danger, and nothing can be lost. The standard
 * virtual-pet mechanic runs on low-grade guilt aimed at children, and the way to
 * refuse it is to have no state that expresses it.
 */
export const moodOf = (fullness, playDay) => fullness.fedThrough >= playDay ? 'happy' : 'napping';
/** Play-days of food left, floored at zero. */
export const daysLeft = (fullness, playDay) => Math.max(0, fullness.fedThrough - playDay + 1);
/**
 * Feed it.
 *
 * Food **stacks from whichever is later** — today, or the day it was already
 * fed through — so feeding a happy pet is never wasted, and a child who buys
 * two Honeycombs gets eight play-days rather than four. Nothing here can reduce
 * `fedThrough`.
 */
export function feed(fullness, food, playDay) {
    const from = Math.max(fullness.fedThrough + 1, playDay);
    return { fedThrough: from + FOOD[food].playDays - 1 };
}
// --- applying a buff --------------------------------------------------------
/**
 * The buff in force, which is *no buff at all* unless a pet is active and fed.
 *
 * Napping pauses the bonus and that is the entire consequence of not feeding —
 * stated as the one branch that produces `null`, so there is no path where a
 * hungry pet costs a child anything.
 */
export function activeBuff(pet, fullness, playDay, 
/** Prism's pick for today. Ignored by every other pet. */
mirroring) {
    if (pet === null || fullness === null)
        return null;
    if (moodOf(fullness, playDay) !== 'happy')
        return null;
    if (pet.buff.surface !== 'mirror')
        return pet.buff;
    // Nothing picked yet today is no bonus, not a default one: Prism's whole
    // character is the choosing, and quietly choosing for them takes it away.
    const borrowed = mirroring == null ? undefined : petById(mirroring);
    // A mirror pointed at a mirror is not a bonus, and is the only way this
    // could fail to terminate.
    return borrowed === undefined || borrowed.buff.surface === 'mirror'
        ? null
        : borrowed.buff;
}
/**
 * Apply a buff to what a lesson earned.
 *
 * Takes and returns `Earned`, which holds xp, acorns and three facts about how
 * the lesson went — so this function *cannot* change a question, a hint, or
 * whether an answer was right, because none of those are in scope. The
 * exhaustive switch is the other half: a new surface added to `BuffSurface`
 * fails to compile here until somebody decides what it does.
 *
 * Rounded down. A buff should never be the reason a number looks odd.
 */
export function buffEarnings(earned, buff, payout = 'lesson') {
    if (buff === null)
        return earned;
    switch (buff.surface) {
        case 'rewards':
            return covers(buff.stream, payout)
                ? {
                    ...earned,
                    xp: Math.floor(earned.xp * buff.xp),
                    acorns: Math.floor(earned.acorns * buff.acorns),
                }
                : earned;
        // Everything else changes the *shape* of a lesson rather than its payout,
        // and is read where that shape is decided: `practice` by the builder,
        // `combo` by the combo, `bonusRounds` by the path. `mirror` never survives
        // `activeBuff`, which resolves it to the borrowed pet's own buff.
        case 'practice':
        case 'combo':
        case 'bonusRounds':
        case 'mirror':
            return earned;
    }
}
/** Extra review items this buff is worth, for the lesson builder. */
export const extraReviewItems = (buff) => buff?.surface === 'practice' ? buff.extraReviewItems : 0;
/** Whether a first mistake is forgiven, for the combo. */
export const forgivesFirstMistake = (buff) => buff?.surface === 'combo';
/** How much more often a bonus round should appear. */
export const bonusRoundMultiplier = (buff) => buff?.surface === 'bonusRounds' ? buff.multiplier : 1;
//# sourceMappingURL=pets.js.map